import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import debounce from 'lodash/debounce';
import { get } from 'utils';

import { DashFilterButtonsUI } from './ui';

export class DashFilterButtons extends PureComponent {
    static displayName = 'DashFilterButtonsContainer';

    static propTypes = {
        dashBookmarkTags: PropTypes.arrayOf(PropTypes.any),
        dashFilters: PropTypes.arrayOf(PropTypes.any),
        dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])),
        equityScope: PropTypes.arrayOf(PropTypes.any),
        hasRefEquityScope: PropTypes.bool,
        name: PropTypes.string,
        onChange: PropTypes.func,
        styles: PropTypes.objectOf(PropTypes.any)
    };

    static defaultProps = {
        dashBookmarkTags: null,
        dashFilters: null,
        dateRange: null,
        equityScope: null,
        hasRefEquityScope: false,
        name: 'dashFilters',
        onChange: null,
        styles: undefined
    };

    constructor(props) {
        super(props);

        this.handleButtonRefs = this.handleButtonRefs.bind(this);
        this.handleScrollContainer = this.handleScrollContainer.bind(this);
        this.onRemove = this.onRemove.bind(this);
        this.checkButtonVisibility = debounce(this.checkButtonVisibility.bind(this), 250, { trailing: true });
        this.removeBookmarkTag = this.removeBookmarkTag.bind(this);
        this.removeDateRange = this.removeDateRange.bind(this);
        this.removeEquityScope = this.removeEquityScope.bind(this);
        this.scrollLeft = this.scrollLeft.bind(this);
        this.scrollRight = this.scrollRight.bind(this);

        this.buttons = {};
        this.scrollContainer = null;

        this.state = {
            canScrollLeft: false,
            canScrollRight: false
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.checkButtonVisibility);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkButtonVisibility);
    }

    handleButtonRefs(node, type, index) {
        const { canScrollRight } = this.state;
        if (!this.buttons[type]) {
            this.buttons[type] = [];
        }
        this.buttons[type][index] = node;

        // Check if can scroll
        // on button dom mount
        if (!canScrollRight && this.scrollContainer && node) {
            const { right } = this.scrollContainer.getBoundingClientRect();
            const { left: buttonLeft, right: buttonRight } = node.getBoundingClientRect();
            if (buttonLeft - 4 > right || buttonRight - 4 > right) {
                this.setState({ canScrollRight: true });
            }
        }
    }

    handleScrollContainer(node) {
        this.scrollContainer = node;
    }

    scrollLeft() {
        const { left } = this.scrollContainer.getBoundingClientRect();
        // Put all buttons in proper order
        const buttons = [
            ...get(this.buttons, 'dateRange', []),
            ...get(this.buttons, 'equityScope', []),
            ...get(this.buttons, 'filters', []),
            ...get(this.buttons, 'dashBookmarkTags', [])
        ].reverse();

        let scrollToNode = null;

        // eslint-disable-next-line
        for (let i = 0; i < buttons.length; i++) {
            const node = buttons[i];
            if (node) {
                const { left: buttonLeft, right: buttonRight } = node.getBoundingClientRect();
                // Check if button is totally out of view
                if (buttonRight < left) {
                    scrollToNode = node;
                    break;
                    // Check if button is partially out of view
                } else if (buttonLeft < left) {
                    scrollToNode = node;
                }
            }
        }

        // Scroll to first out of view button, or only partially out of view button
        if (scrollToNode) {
            scrollToNode.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }

    scrollRight() {
        const { right } = this.scrollContainer.getBoundingClientRect();
        // Put all buttons in proper order
        const buttons = [
            ...get(this.buttons, 'dateRange', []),
            ...get(this.buttons, 'equityScope', []),
            ...get(this.buttons, 'filters', []),
            ...get(this.buttons, 'dashBookmarkTags', [])
        ];

        let scrollToNode = null;

        // eslint-disable-next-line
        for (let i = 0; i < buttons.length; i++) {
            const node = buttons[i];
            if (node) {
                const { left: buttonLeft, right: buttonRight } = node.getBoundingClientRect();
                // Check if button is totally out of view
                if (buttonLeft > right) {
                    scrollToNode = node;
                    break;
                    // Check if button is partially out of view
                } else if (buttonRight > right) {
                    scrollToNode = node;
                }
            }
        }

        // Scroll to first out of view button, or only partially out of view button
        if (scrollToNode) {
            scrollToNode.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }

    onRemove(filter) {
        const { dashFilters, name, onChange } = this.props;
        if (onChange) {
            onChange({ name, value: dashFilters.filter(f => f !== filter) });
            this.checkButtonVisibility();
        }
    }

    checkButtonVisibility() {
        if (this.scrollContainer && this.scrollContainer.getBoundingClientRect) {
            const { right, left } = this.scrollContainer.getBoundingClientRect();
            // Put all buttons in proper order
            const buttons = [
                ...get(this.buttons, 'dateRange', []),
                ...get(this.buttons, 'equityScope', []),
                ...get(this.buttons, 'filters', []),
                ...get(this.buttons, 'dashBookmarkTags', [])
            ];

            let scrollLeft = false;
            let scrollRight = false;

            // eslint-disable-next-line
            for (let i = 0; i < buttons.length; i++) {
                const node = buttons[i];
                if (node) {
                    const { left: buttonLeft, right: buttonRight } = node.getBoundingClientRect();
                    if (!scrollRight && (buttonLeft - 4 > right || buttonRight - 4 > right)) {
                        scrollRight = true;
                    }
                    if (!scrollLeft && (buttonRight < left || buttonLeft < left)) {
                        scrollLeft = true;
                    }

                    if (scrollLeft && scrollRight) {
                        break;
                    }
                }
            }

            this.setState({
                canScrollRight: scrollRight,
                canScrollLeft: scrollLeft
            });
        }
    }

    removeBookmarkTag(tag) {
        const { dashBookmarkTags, onChange } = this.props;
        onChange({ name: 'dashBookmarkTags', value: dashBookmarkTags.filter(t => t !== tag) });
        this.checkButtonVisibility();
    }

    removeDateRange(event) {
        const { onChange } = this.props;
        onChange({ name: 'dashDateRange', value: null, event });
        this.checkButtonVisibility();
    }

    removeEquityScope({ type, value, event }) {
        const { onChange, equityScope, hasRefEquityScope } = this.props;

        const newEquityScope = equityScope.filter(({ value: scopeValue, type: scopeType }) => {
            if (type === scopeType) {
                if (type !== 'sector' && value === scopeValue) {
                    return false;
                }

                if (type === 'sector') {
                    if (
                        (get(value, 'gicsSubSectorId') &&
                            get(value, 'gicsSubSectorId') === get(scopeValue, 'gicsSubSectorId')) ||
                        (get(value, 'gicsSectorId') && get(value, 'gicsSectorId') === get(scopeValue, 'gicsSectorId'))
                    ) {
                        return false;
                    }
                }

                return true;
            }

            return true;
        });

        onChange({ name: hasRefEquityScope ? 'refEquityScope' : 'dashEquityScope', value: newEquityScope, event });
        this.checkButtonVisibility();
    }

    render() {
        const { dashBookmarkTags, dashFilters, styles, equityScope, dateRange, onChange } = this.props;
        const { canScrollLeft, canScrollRight } = this.state;
        return (
            <DashFilterButtonsUI
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                dashBookmarkTags={dashBookmarkTags}
                dashFilters={dashFilters}
                dateRange={dateRange}
                equityScope={equityScope}
                handleButtonRefs={this.handleButtonRefs}
                handleScrollContainer={this.handleScrollContainer}
                onChange={onChange}
                onRemove={this.onRemove}
                onScroll={this.checkButtonVisibility}
                removeBookmarkTag={this.removeBookmarkTag}
                removeDateRange={this.removeDateRange}
                removeEquityScope={this.removeEquityScope}
                scrollLeft={this.scrollLeft}
                scrollRight={this.scrollRight}
                styles={styles}
            />
        );
    }
}

export const DashFilterButtonsContainer = compose()(DashFilterButtons);
